import type { UseQueryResult } from '@tanstack/react-query';
import { useDerivedNetworkContext, useFilterContext } from '@/context';
import { hourlyPlaysFetcher } from '@/services/revenueApi/content/fetchers';
import { useApi } from '@/services/queryUtils';
import type { HourlyPlaysHour } from '@/types/content';

/**
 * Live downloads for the last 24 UTC hours.
 * Ignores the page date filter. The chart converts each `hour_utc` to the browser timezone.
 */
export const useContentPerformanceLiveDownloadsQuery = ({
  enabled,
}: {
  enabled: boolean;
}): UseQueryResult<HourlyPlaysHour[], Error> => {
  const { filters } = useFilterContext();
  const { selectedNetwork, selectedNetworks } = useDerivedNetworkContext();
  const showIds = filters.selectedShows.length > 0 ? filters.selectedShows : undefined;
  const networkIds = selectedNetworks
    .map((network) => network.id)
    .filter((networkId): networkId is string => Boolean(networkId));
  const networkId = selectedNetwork.id ?? '';
  const networkIdsKey = networkIds.join(',') || networkId;

  return useApi(
    ['hourly-plays', networkIdsKey, showIds?.join(',') ?? ''],
    hourlyPlaysFetcher({
      networkId,
      networkIds: networkIds.length > 0 ? networkIds : undefined,
      showIds,
    }),
    {
      enabled: enabled && Boolean(networkId),
      // Refetch every 5 minutes. The gold table is rewritten each hour.
      refetchInterval: 5 * 60 * 1000,
    }
  );
};
